/**
 * Milestone order as a graph: which milestones come after which.
 *
 * Pure functions over a map of milestone id to the ids it directly comes after. The order is a
 * partial order, so two milestones with no path between them are parallel tracks and neither
 * waits for the other. Everything here terminates on data that already contains a cycle,
 * because the table can only refuse new ones.
 */

/** Milestone id → the milestones it directly comes after. */
export type MilestoneOrder = ReadonlyMap<number, readonly number[]>;

/** Every milestone `id` comes after, directly or through another milestone. Excludes `id`. */
export function earlierMilestones(order: MilestoneOrder, id: number): Set<number> {
  const seen = new Set<number>();
  const pending = [...(order.get(id) ?? [])];
  while (pending.length > 0) {
    const next = pending.pop()!;
    if (next === id || seen.has(next)) continue;
    seen.add(next);
    pending.push(...(order.get(next) ?? []));
  }
  return seen;
}

/** True when `later` comes after `earlier`, directly or transitively. */
export function comesAfter(order: MilestoneOrder, later: number, earlier: number): boolean {
  return later !== earlier && earlierMilestones(order, later).has(earlier);
}

/**
 * The cycle that giving `id` these earlier milestones would close, or undefined.
 *
 * @returns the path from `id` back to itself, `id` first and last.
 */
export function orderCycle(order: MilestoneOrder, id: number, after: readonly number[]): number[] | undefined {
  for (const start of after) {
    if (start === id) return [id, id];
    const stack: number[][] = [[start]];
    const visited = new Set<number>();
    while (stack.length > 0) {
      const path = stack.pop()!;
      const head = path[path.length - 1]!;
      if (head === id) return [id, ...path];
      if (visited.has(head)) continue;
      visited.add(head);
      for (const next of order.get(head) ?? []) stack.push([...path, next]);
    }
  }
  return undefined;
}

/**
 * Depth in the order: 0 for a milestone that comes after nothing, otherwise one more than the
 * deepest milestone it comes after. Used to lay milestones out left to right.
 */
export function milestoneRanks(ids: readonly number[], order: MilestoneOrder): Map<number, number> {
  const ranks = new Map<number, number>();
  const visiting = new Set<number>();
  const rankOf = (id: number): number => {
    const known = ranks.get(id);
    if (known !== undefined) return known;
    // A cycle in stored data ranks as a root rather than recursing forever.
    if (visiting.has(id)) return 0;
    visiting.add(id);
    let rank = 0;
    for (const earlier of order.get(id) ?? []) rank = Math.max(rank, rankOf(earlier) + 1);
    visiting.delete(id);
    ranks.set(id, rank);
    return rank;
  };
  for (const id of ids) rankOf(id);
  return ranks;
}

/**
 * Drop every direct relation another path already implies.
 *
 * "M7 after M6" and "M6 after M5" make "M7 after M5" redundant; keeping it would show a person
 * three relations where two say the same thing.
 */
export function reduceMilestoneOrder(order: MilestoneOrder): Map<number, number[]> {
  const reduced = new Map<number, number[]>();
  for (const [id, direct] of order) {
    const unique = [...new Set(direct)].filter((earlier) => earlier !== id);
    const kept = unique.filter(
      (earlier) => !unique.some((other) => other !== earlier && earlierMilestones(order, other).has(earlier)),
    );
    if (kept.length > 0) reduced.set(id, kept.sort((a, b) => a - b));
  }
  return reduced;
}

export interface MilestoneEdgeSample {
  /** Milestone of the ticket that waits. */
  dependentMilestoneId: number;
  /** Milestone of the ticket it waits for. */
  prerequisiteMilestoneId: number;
}

export interface DerivedMilestoneOrder {
  /** Transitively reduced: milestone id → the milestones it directly comes after. */
  order: Map<number, number[]>;
  /** Milestone pairs with ticket links both ways, left out; `[later, earlier]` as the links mostly said. */
  conflicts: Array<{ later: number; earlier: number; links: number; against: number }>;
  /** Ticket links behind each accepted direct relation before reduction, keyed `later:earlier`. */
  weights: Map<string, number>;
}

/**
 * Infer an order from the ticket dependencies that cross milestones.
 *
 * A pair is accepted in descending order of how many ticket links support it, and refused
 * when it would close a cycle with what is already accepted. Refused pairs are returned rather
 * than dropped silently, because they are the places the plan disagrees with itself.
 */
export function deriveMilestoneOrder(edges: readonly MilestoneEdgeSample[]): DerivedMilestoneOrder {
  const weights = new Map<string, number>();
  for (const edge of edges) {
    if (edge.dependentMilestoneId === edge.prerequisiteMilestoneId) continue;
    const key = `${edge.dependentMilestoneId}:${edge.prerequisiteMilestoneId}`;
    weights.set(key, (weights.get(key) ?? 0) + 1);
  }
  const pairs = [...weights.entries()]
    .map(([key, links]) => {
      const [later, earlier] = key.split(':').map(Number) as [number, number];
      return { later, earlier, links, against: weights.get(`${earlier}:${later}`) ?? 0 };
    })
    .sort((a, b) => b.links - b.against - (a.links - a.against) || b.links - a.links || a.later - b.later || a.earlier - b.earlier);

  const accepted = new Map<number, number[]>();
  const conflicts: DerivedMilestoneOrder['conflicts'] = [];
  for (const pair of pairs) {
    if (orderCycle(accepted, pair.later, [pair.earlier])) {
      conflicts.push(pair);
      continue;
    }
    accepted.set(pair.later, [...(accepted.get(pair.later) ?? []), pair.earlier]);
  }
  const acceptedWeights = new Map<string, number>();
  for (const [later, earlier] of [...accepted].flatMap(([id, list]) => list.map((e) => [id, e] as const))) {
    acceptedWeights.set(`${later}:${earlier}`, weights.get(`${later}:${earlier}`) ?? 0);
  }
  return { order: reduceMilestoneOrder(accepted), conflicts, weights: acceptedWeights };
}

/* ── placement with phases ─────────────────────────────────────────────────────────────── */

/** Phase id → the phases it is planned after. Order only: it places and advises, never blocks. */
type PhaseOrder = ReadonlyMap<number, readonly number[]>;

/** What placement needs: which phase each milestone is in, and both orders. */
export interface PlacementInput {
  /** Every milestone, with the registered phase it belongs to, or null. */
  milestones: ReadonlyArray<{ id: number; phaseId: number | null }>;
  /** Every registered phase, empty ones included: an empty phase still links its neighbours. */
  phaseIds: readonly number[];
  /** Milestone id → the milestones it comes after, blocking and order only together. */
  milestoneOrder: MilestoneOrder;
  phaseOrder: PhaseOrder;
}

type PlacementNode = `m:${number}` | `in:${number}` | `out:${number}`;

/**
 * The placement graph: every milestone, and an entry and an exit for every registered phase.
 *
 * A milestone sits after its phase's entry and before its exit; a phase link runs from the earlier
 * phase's exit to the later phase's entry. Solving one graph keeps every milestone link and every
 * phase boundary true together, where laying phases out one at a time and patching the links
 * between them does not. Each predecessor is listed with its width: a milestone takes a column, an
 * entry or exit takes none, so an empty phase in a chain passes the order through without a gap.
 */
function placementGraph(input: PlacementInput): Map<PlacementNode, Array<{ from: PlacementNode; width: 0 | 1 }>> {
  const preds = new Map<PlacementNode, Array<{ from: PlacementNode; width: 0 | 1 }>>();
  const add = (to: PlacementNode, from: PlacementNode, width: 0 | 1): void => {
    const list = preds.get(to) ?? [];
    list.push({ from, width });
    preds.set(to, list);
  };
  const phases = new Set(input.phaseIds);
  for (const id of input.phaseIds) {
    preds.set(`in:${id}`, preds.get(`in:${id}`) ?? []);
    add(`out:${id}`, `in:${id}`, 0);
  }
  const milestones = new Set(input.milestones.map((row) => row.id));
  for (const row of input.milestones) {
    preds.set(`m:${row.id}`, preds.get(`m:${row.id}`) ?? []);
    for (const earlier of input.milestoneOrder.get(row.id) ?? []) {
      if (milestones.has(earlier) && earlier !== row.id) add(`m:${row.id}`, `m:${earlier}`, 1);
    }
    if (row.phaseId !== null && phases.has(row.phaseId)) {
      add(`m:${row.id}`, `in:${row.phaseId}`, 0);
      add(`out:${row.phaseId}`, `m:${row.id}`, 1);
    }
  }
  for (const [later, earlierList] of input.phaseOrder) {
    if (!phases.has(later)) continue;
    for (const earlier of earlierList) {
      if (phases.has(earlier) && earlier !== later) add(`in:${later}`, `out:${earlier}`, 0);
    }
  }
  return preds;
}

/**
 * The column of every milestone: the longest path to it, counting milestones only.
 *
 * With no phase links every entry has nothing before it, so a milestone's column is exactly its
 * {@link milestoneRanks} rank; phase labels alone change nothing. A cycle in stored data ranks as a
 * root, as `milestoneRanks` does, rather than recursing forever.
 */
export function placementColumns(input: PlacementInput): Map<number, number> {
  const preds = placementGraph(input);
  const value = new Map<PlacementNode, number>();
  const visiting = new Set<PlacementNode>();
  const valueOf = (node: PlacementNode): number => {
    const known = value.get(node);
    if (known !== undefined) return known;
    if (visiting.has(node)) return 0;
    visiting.add(node);
    let result = 0;
    for (const pred of preds.get(node) ?? []) result = Math.max(result, valueOf(pred.from) + pred.width);
    visiting.delete(node);
    value.set(node, result);
    return result;
  };
  const columns = new Map<number, number>();
  for (const row of input.milestones) columns.set(row.id, valueOf(`m:${row.id}`));
  return columns;
}

/** One step of a placement cycle, named for a message. */
export type PlacementStep = { kind: 'milestone'; id: number } | { kind: 'phase'; id: number };

/**
 * A cycle in the placement graph, or undefined. Checked over every registered phase, empty ones
 * too, so a link that is harmless while a phase is empty cannot close a cycle when it fills.
 *
 * @returns the milestones and phases on the cycle, in order, each named once.
 */
export function placementCycle(input: PlacementInput): PlacementStep[] | undefined {
  const preds = placementGraph(input);
  const state = new Map<PlacementNode, 1 | 2>();
  const stack: PlacementNode[] = [];
  let found: PlacementNode[] | undefined;
  const visit = (node: PlacementNode): boolean => {
    state.set(node, 1);
    stack.push(node);
    for (const pred of preds.get(node) ?? []) {
      const seen = state.get(pred.from);
      if (seen === 1) {
        found = stack.slice(stack.indexOf(pred.from));
        return true;
      }
      if (seen === undefined && visit(pred.from)) return true;
    }
    stack.pop();
    state.set(node, 2);
    return false;
  };
  for (const node of preds.keys()) {
    if (!state.has(node) && visit(node)) break;
  }
  if (!found) return undefined;
  const steps: PlacementStep[] = [];
  for (const node of found.reverse()) {
    const [kind, raw] = node.split(':') as [string, string];
    const step: PlacementStep = kind === 'm' ? { kind: 'milestone', id: Number(raw) } : { kind: 'phase', id: Number(raw) };
    const last = steps[steps.length - 1];
    if (!last || last.kind !== step.kind || last.id !== step.id) steps.push(step);
  }
  return steps;
}

/**
 * Whether any phase that holds milestones is planned after another that does, directly or through
 * empty phases. Links among empty phases alone change nothing on screen, so they do not switch a
 * graph from its bands to phase flow.
 */
export function hasActivePhaseOrder(activePhaseIds: ReadonlySet<number>, phaseOrder: PhaseOrder): boolean {
  for (const phase of activePhaseIds) {
    const pending = [...(phaseOrder.get(phase) ?? [])];
    const seen = new Set<number>();
    while (pending.length > 0) {
      const earlier = pending.pop()!;
      if (seen.has(earlier) || earlier === phase) continue;
      seen.add(earlier);
      if (activePhaseIds.has(earlier)) return true;
      pending.push(...(phaseOrder.get(earlier) ?? []));
    }
  }
  return false;
}

/** Where a milestone sits: its column, and the lane (horizontal band) it shares with other phases. */
export interface MilestonePlacement {
  column: number;
  lane: number;
}

const placementCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * Columns and lanes for projects whose phases are linked.
 *
 * Phases are taken in order of their first column, then name. Each goes into the first lane whose
 * last used column is before its own first column, so a chain of phases runs along one lane left to
 * right and phases that overlap in columns stack as bands. Milestones with no phase take the last
 * lane. Callers use this only when the project has phase links: without them the graph keeps its
 * one band per phase.
 */
export function placeMilestones(
  input: PlacementInput & { phaseNames: ReadonlyMap<number, string> },
): Map<number, MilestonePlacement> {
  const columns = placementColumns(input);
  const spans = new Map<number, { first: number; last: number }>();
  for (const row of input.milestones) {
    if (row.phaseId === null) continue;
    const column = columns.get(row.id) ?? 0;
    const span = spans.get(row.phaseId);
    spans.set(row.phaseId, span ? { first: Math.min(span.first, column), last: Math.max(span.last, column) } : { first: column, last: column });
  }
  const ordered = [...spans.entries()].sort(([a, sa], [b, sb]) =>
    sa.first - sb.first || placementCollator.compare(input.phaseNames.get(a) ?? '', input.phaseNames.get(b) ?? '') || a - b);
  const laneEnds: number[] = [];
  const laneOf = new Map<number, number>();
  for (const [phaseId, span] of ordered) {
    let lane = laneEnds.findIndex((end) => end < span.first);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(span.last);
    } else laneEnds[lane] = span.last;
    laneOf.set(phaseId, lane);
  }
  const unphasedLane = laneEnds.length;
  const placements = new Map<number, MilestonePlacement>();
  for (const row of input.milestones) {
    placements.set(row.id, {
      column: columns.get(row.id) ?? 0,
      lane: row.phaseId === null || !laneOf.has(row.phaseId) ? unphasedLane : laneOf.get(row.phaseId)!,
    });
  }
  return placements;
}
